import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Chart, registerables } from 'chart.js';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AppState } from './../../../../App.state';
import { NovoDashboardService } from 'src/app/base/services/novo-dashboard.service';
import { DialogService } from 'src/app/global/services/dialog.service';
import { QueryOptions } from 'src/app/global/domain/query.options';
import { NovatosCobrancasRenovacoes } from 'src/app/base/domain/dto/novatos-cobrancas-renovacoes.dto';

Chart.register(...registerables);

const CATEGORIA_LABEL: { [key: string]: string } = {
  NOVATO: 'Novatos',
  COBRANCA: 'Cobranças',
  RENOVACAO: 'Renovações',
};

const CATEGORIA_COR: { [key: string]: string } = {
  NOVATO: '#4caf50',
  COBRANCA: '#f44336',
  RENOVACAO: '#ff9800',
};

@Component({
  selector: 'app-dashboard-novatos-cobrancas-renovacoes',
  templateUrl: './dashboard-novatos-cobrancas-renovacoes.component.html',
  styleUrls: ['./dashboard-novatos-cobrancas-renovacoes.component.css']
})
export class DashboardNovatosCobrancasRenovacoesComponent implements OnInit, OnDestroy {

  @ViewChild('graficoNovatosCobrancasRenovacoes', { static: true })
  canvas!: ElementRef<HTMLCanvasElement>;

  categoria: string = '';
  estado: string = '';
  posto: string = '';
  rota: string = '';
  nomeCliente: string = '';
  dataInicio: string = '';
  dataFim: string = '';
  postosDisponiveis: string[] = [];
  rotasDisponiveis: string[] = [];
  queryOptions = new QueryOptions({ pageSize: 100 });
  data: NovatosCobrancasRenovacoes[] = [];
  chart?: Chart;

  filteredData: NovatosCobrancasRenovacoes[] = [];
  paginatedData: NovatosCobrancasRenovacoes[] = [];
  totalPages: number = 1;
  currentPage: number = 1;
  pageSize: number = 10;

  private searchInput$ = new Subject<string>();

  constructor(
    private service: NovoDashboardService,
    private appState: AppState,
    public dialogService: DialogService
  ) { }

  ngOnInit(): void {
    this.loadFilters();
    this.loadDashboard();

    this.searchInput$.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe((term: string) => {
      this.nomeCliente = term;
      this.currentPage = 1;
      this.applyFilter();
    });
  }

  ngOnDestroy(): void {
    this.searchInput$.complete();
  }

  onSearchInput(term: string): void {
    this.searchInput$.next(term);
  }

  private loadFilters(): void {
    this.service.findAllPostosNovatosCobrancasRenovacoes().subscribe(res => this.postosDisponiveis = res);
    this.service.findAllRotasNovatosCobrancasRenovacoes().subscribe(res => this.rotasDisponiveis = res);
  }

  public loadDashboard(): void {
    this.appState.isLoading = true;
    const dInicio = (this.dataInicio && this.dataInicio.length === 10) ? `${this.dataInicio}T00:00:00` : this.dataInicio;
    const dFim = (this.dataFim && this.dataFim.length === 10) ? `${this.dataFim}T23:59:59` : this.dataFim;

    this.queryOptions.params = {
      categoria: this.categoria,
      estado: this.estado,
      posto: this.posto,
      rota: this.rota,
      nomeCliente: this.nomeCliente,
      dataInicio: dInicio,
      dataFim: dFim,
    };

    this.service.findAllNovatosCobrancasRenovacoes(this.queryOptions).subscribe({
      next: (response: NovatosCobrancasRenovacoes[]) => {
        this.data = response;
        this.currentPage = 1;
        this.applyFilter();
        this.createChart();
        this.appState.isLoading = false;
      },
      error: () => {
        this.dialogService.feedbackError('Erro ao carregar o dashboard de novatos, cobranças e renovações.');
        this.appState.isLoading = false;
      }
    });
  }

  private applyFilter(): void {
    const term = (this.nomeCliente || '').toLowerCase().trim();
    this.filteredData = !term ? this.data : this.data.filter(i =>
      (i.nomeCliente || '').toLowerCase().includes(term) ||
      (i.uid || '').toLowerCase().includes(term)
    );
    this.totalPages = Math.ceil(this.filteredData.length / this.pageSize) || 1;
    this.updatePage();
  }

  private updatePage(): void {
    const start = (this.currentPage - 1) * this.pageSize;
    this.paginatedData = this.filteredData.slice(start, start + this.pageSize);
  }

  private createChart(): void {
    if (this.chart) {
      this.chart.destroy();
    }

    const estados = Array.from(new Set(this.data.map(i => i.estado || 'N/D'))).sort();
    const categorias = ['NOVATO', 'COBRANCA', 'RENOVACAO'];

    const datasets = categorias.map(cat => ({
      label: CATEGORIA_LABEL[cat],
      data: estados.map(uf => this.data.filter(i => (i.estado || 'N/D') === uf && i.categoria === cat).length),
      backgroundColor: CATEGORIA_COR[cat],
    }));

    this.chart = new Chart(this.canvas.nativeElement, {
      type: 'bar',
      data: { labels: estados, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: { beginAtZero: true, ticks: { precision: 0 } }
        },
        plugins: {
          title: { display: true, text: 'Quantidade de contratos por categoria e estado' }
        }
      }
    });
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePage();
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePage();
    }
  }

  categoriaLabel(categoria: string): string {
    return CATEGORIA_LABEL[categoria] || categoria;
  }
}
